/**
 * Participantes identificados na transcrição (Nome (papel): …)
 * + confirmação/ajuste. Amostras de áudio por falante só com timestamps reais
 * (diarização) — fatias artificiais enganam o vínculo.
 */

import i18n from "../../utils/i18n";

export function normalizeNameKey(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Normaliza labels de papel (parentesco/cargo; responsabilidade é campo à parte). */
const ROLE_I18N = {
  mae: "smartMinutes.role.mother",
  pai: "smartMinutes.role.father",
  aluno: "smartMinutes.role.student",
  aluna: "smartMinutes.role.studentFemale",
  responsavel: "smartMinutes.role.guardian",
  "responsavel pedagogico": "smartMinutes.role.pedagogical",
  "responsavel pedagogica": "smartMinutes.role.pedagogical",
  pedagogico: "smartMinutes.role.pedagogical",
  pedagogica: "smartMinutes.role.pedagogical",
  financeiro: "smartMinutes.role.financial",
  "responsavel financeiro": "smartMinutes.role.financial",
  "resp financeiro": "smartMinutes.role.financial",
  coordenacao: "smartMinutes.role.coordination",
  coordenador: "smartMinutes.role.coordination",
  coordenadora: "smartMinutes.role.coordination",
  orientacao: "smartMinutes.role.guidance",
  professor: "smartMinutes.role.teacher",
  professora: "smartMinutes.role.teacher",
  "professor(a)": "smartMinutes.role.teacher",
  "professor(a) - operador": "smartMinutes.role.teacherOperator",
  "professor(a) — operador": "smartMinutes.role.teacherOperator",
  funcionario: "smartMinutes.role.staff",
  colaborador: "smartMinutes.role.collaborator"
};

export function formatParticipantRole(role) {
  const raw = String(role || "").trim();
  if (!raw) return i18n.t("smartMinutes.role.participantLower");
  const key = normalizeNameKey(raw);
  if (ROLE_I18N[key]) return i18n.t(ROLE_I18N[key]);
  if (/pedagog/.test(key)) return i18n.t("smartMinutes.role.pedagogical");
  if (/financeir/.test(key)) return i18n.t("smartMinutes.role.financial");
  return raw;
}

/** pedagogico | financeiro | null */
export function parseResponsibilityKey(text) {
  const key = normalizeNameKey(text);
  if (!key) return null;
  if (/financeir/.test(key)) return "financeiro";
  if (/pedagog|academico/.test(key)) return "pedagogico";
  return null;
}

export function formatResponsibilityLabel(kind) {
  if (kind === "pedagogico")
    return i18n.t("smartMinutes.role.pedagogicalShort");
  if (kind === "financeiro") return i18n.t("smartMinutes.role.financialShort");
  return null;
}

function inferRoleFromSpeakerLabel(name) {
  const key = normalizeNameKey(name);
  if (/professor|prof\b/.test(key)) return "professora";
  if (/coordenad/.test(key)) return "coordenação";
  if (/\bmae\b|mother/.test(key)) return "mãe";
  if (/\bpai\b|father/.test(key)) return "pai";
  if (/alun[oa]/.test(key)) return "aluno";
  if (/pedagog/.test(key)) return "responsável pedagógico";
  if (/financeir/.test(key)) return "financeiro";
  if (/orienta/.test(key)) return i18n.t("smartMinutes.role.guidance");
  return i18n.t("smartMinutes.role.participantLower");
}

/** Opções editáveis de timbre/tom (lab — até haver análise acústica real). */
const VOICE_DESCRIPTOR_KEYS = [
  "smartMinutes.voiceDescriptor.femaleCollaborative",
  "smartMinutes.voiceDescriptor.femaleSerious",
  "smartMinutes.voiceDescriptor.femaleFirm",
  "smartMinutes.voiceDescriptor.maleObjective",
  "smartMinutes.voiceDescriptor.maleCalm",
  "smartMinutes.voiceDescriptor.childShy",
  "smartMinutes.voiceDescriptor.childLively"
];

export function voiceDescriptorOptions() {
  return VOICE_DESCRIPTOR_KEYS.map(key => i18n.t(key));
}

export const VOICE_DESCRIPTOR_OPTIONS = voiceDescriptorOptions();

/** Fallback por índice: só tons adultos (infantil só com papel de aluno). */
const ADULT_VOICE_DESCRIPTOR_KEYS = VOICE_DESCRIPTOR_KEYS.filter(
  key =>
    key !== "smartMinutes.voiceDescriptor.childShy" &&
    key !== "smartMinutes.voiceDescriptor.childLively"
);

export function voiceDescriptorForIndex(index) {
  const list = ADULT_VOICE_DESCRIPTOR_KEYS.length
    ? ADULT_VOICE_DESCRIPTOR_KEYS.map(key => i18n.t(key))
    : voiceDescriptorOptions();
  return list[Number(index) % list.length];
}

/**
 * Infere timbre/tom a partir de nome/papel do participante (lab).
 * Papéis adultos da escola têm prioridade sobre "Infantil" no cargo
 * (ex.: "Ana Coordenadora Infantil" ≠ voz de criança).
 */
export function inferVoiceDescriptorHint({ name, role, responsibility } = {}) {
  const nameKey = normalizeNameKey(name);
  const roleKey = normalizeNameKey(role);
  const respKey = normalizeNameKey(responsibility);
  const blob = [nameKey, roleKey, respKey].filter(Boolean).join(" ");
  if (!blob) return null;

  const adultStaff =
    /\bcoordenad/.test(blob) ||
    /\bprofessor/.test(blob) ||
    /\borienta/.test(blob) ||
    /\bpedagog/.test(blob) ||
    /\bfinanceir/.test(blob) ||
    /\boperador/.test(blob) ||
    /\bsecretar/.test(blob) ||
    /\bcolaborador/.test(blob) ||
    /\bfuncionario/.test(blob);

  const isChild =
    !adultStaff &&
    (/\balun[oa]\b/.test(roleKey) ||
      /\bcrianca\b/.test(blob) ||
      /\bfilh[oa]\b/.test(blob) ||
      (/\balun[oa]\b/.test(nameKey) &&
        !/\bcoordenad|\bprofessor/.test(nameKey)));

  if (isChild) {
    return i18n.t("smartMinutes.voiceDescriptor.childShy");
  }

  if (
    /\bpai\b/.test(roleKey) ||
    /\btio\b/.test(roleKey) ||
    /\bavo\b/.test(roleKey) ||
    /\bmasculin/.test(blob) ||
    /\bemanuel\b/.test(nameKey) ||
    /\bjose\b/.test(nameKey) ||
    /\bjoao\b/.test(nameKey) ||
    /\bpedro\b/.test(nameKey) ||
    /\bcarlos\b/.test(nameKey) ||
    /\bluis\b|\bluiz\b/.test(nameKey)
  ) {
    return i18n.t("smartMinutes.voiceDescriptor.maleCalm");
  }

  if (/\bcoordenad/.test(blob)) {
    return i18n.t("smartMinutes.voiceDescriptor.femaleSerious");
  }
  if (
    /\bmae\b/.test(roleKey) ||
    /\bavo\b/.test(roleKey) ||
    /\btia\b/.test(roleKey) ||
    /\bfeminin/.test(blob) ||
    /\broberta\b/.test(nameKey) ||
    /\belisa\b/.test(nameKey) ||
    /\bana\b/.test(nameKey) ||
    /\bmaria\b/.test(nameKey) ||
    /\bprofessor/.test(blob) ||
    /\borienta/.test(blob) ||
    /\bpedagog/.test(blob) ||
    adultStaff
  ) {
    return i18n.t("smartMinutes.voiceDescriptor.femaleCollaborative");
  }
  return null;
}

/**
 * Tom pré-selecionado para o card/seletor.
 * Manual/confirmação prevalecem; senão infere pelo participante sugerido.
 */
export function resolveSpeakerVoiceDescriptor(speaker) {
  if (!speaker) return voiceDescriptorOptions()[0];
  const source = speaker.voiceDescriptorSource;
  if (
    (source === "manual" || source === "person_confirm") &&
    speaker.voiceDescriptor
  ) {
    return speaker.voiceDescriptor;
  }
  const inferred = inferVoiceDescriptorHint({
    name:
      speaker.personName ||
      speaker.suggestedPersonName ||
      speaker.label ||
      speaker.detectedName,
    role:
      speaker.personRole || speaker.suggestedPersonRole || speaker.detectedRole
  });
  if (inferred) return inferred;
  return speaker.voiceDescriptor || voiceDescriptorOptions()[0];
}

export function responsibilityChipStyle(kind) {
  if (kind === "pedagogico") {
    return { background: "#e3f2fd", color: "#0d47a1" };
  }
  if (kind === "financeiro") {
    return { background: "#fff3e0", color: "#e65100" };
  }
  return null;
}

/** Lab: parentesco Alice → tipo de responsável quando o perfil ainda não trouxe. */
const LAB_RESPONSIBILITY_BY_NAME = {
  "roberta martins leite": "pedagogico",
  "emanuel mendes leite": "financeiro"
};

export function getPersonResponsibility(ata, personId, hints = {}) {
  const fromHint =
    parseResponsibilityKey(hints.responsibility) ||
    parseResponsibilityKey(hints.role) ||
    parseResponsibilityKey(hints.relationship);
  if (!ata) return fromHint;

  if (personId) {
    const guardians = (ata.students || []).reduce(
      (acc, s) => acc.concat(s.guardians || []),
      []
    );
    const guardian = guardians.find(g => `guardian:${g.id}` === personId);
    if (guardian) {
      return (
        guardian.responsibility ||
        parseResponsibilityKey(guardian.relationship) ||
        parseResponsibilityKey(guardian.role) ||
        LAB_RESPONSIBILITY_BY_NAME[normalizeNameKey(guardian.name)] ||
        fromHint
      );
    }
    const staff = (ata.staff || []).find(s => `staff:${s.id}` === personId);
    if (staff) {
      return parseResponsibilityKey(staff.role) || fromHint;
    }
  }

  const nameKey = normalizeNameKey(hints.name);
  if (nameKey && LAB_RESPONSIBILITY_BY_NAME[nameKey]) {
    return LAB_RESPONSIBILITY_BY_NAME[nameKey];
  }
  return fromHint;
}

export function listPersonOptions(ata) {
  const options = [];
  const seenIds = new Set();
  const seenNames = new Set();

  const pushUnique = opt => {
    if (!opt || !opt.name) return;
    const id = opt.id != null ? String(opt.id) : "";
    const nameKey = normalizeNameKey(opt.name);
    if (id && seenIds.has(id)) return;
    // Evita Alice/Emanuel/Roberta repetidos N vezes no Select (merge/upsert).
    if (nameKey && seenNames.has(nameKey)) return;
    if (id) seenIds.add(id);
    if (nameKey) seenNames.add(nameKey);
    options.push(opt);
  };

  (ata.staff || []).forEach(s => {
    if (!s) return;
    pushUnique({
      id: `staff:${s.id}`,
      name: s.name,
      role: formatParticipantRole(s.role || "funcionário"),
      responsibility: parseResponsibilityKey(s.role)
    });
  });
  (ata.students || []).forEach(s => {
    if (!s) return;
    pushUnique({
      id: `student:${s.ra}`,
      name: s.name,
      role: formatParticipantRole("aluno"),
      responsibility: null
    });
    (s.guardians || []).forEach(g => {
      if (!g) return;
      pushUnique({
        id: `guardian:${g.id}`,
        name: g.name,
        role: formatParticipantRole(g.role || "responsável"),
        responsibility:
          g.responsibility ||
          parseResponsibilityKey(g.relationship) ||
          parseResponsibilityKey(g.role) ||
          LAB_RESPONSIBILITY_BY_NAME[normalizeNameKey(g.name)] ||
          null
      });
    });
  });
  return options;
}

/** Valor especial do Select: abrir fluxo de inclusão pós-criação. */
export const ADD_PARTICIPANT_VALUE = "__add_participant__";

const GUEST_ROLE_KEYS = [
  "smartMinutes.role.coordination",
  "smartMinutes.role.guidance",
  "smartMinutes.role.teacher",
  "smartMinutes.role.pedagogical",
  "smartMinutes.role.financial",
  "smartMinutes.role.guardian",
  "smartMinutes.role.participant"
];

export function guestRoleOptions() {
  return GUEST_ROLE_KEYS.map(key => i18n.t(key));
}

function personAlreadyListed(ata, name, personId) {
  const options = listPersonOptions(ata);
  if (personId && options.some(o => o.id === personId)) return true;
  const key = normalizeNameKey(name);
  if (!key) return false;
  return options.some(o => normalizeNameKey(o.name) === key);
}

/**
 * Candidatos da turma/cadastro que não entraram na criação da ata
 * (staffPool / guardiansPoolByRa).
 */
export function listAddableParticipants(ata) {
  if (!ata) return [];
  const out = [];
  const seen = new Set();

  (ata.staffPool || []).forEach(s => {
    if (!s || !s.id || !s.name) return;
    const personId = `staff:${s.id}`;
    if (personAlreadyListed(ata, s.name, personId)) return;
    const key = `staff:${s.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      kind: "staff",
      id: s.id,
      name: s.name,
      role: formatParticipantRole(s.role || "funcionário"),
      personId
    });
  });

  const pools = ata.guardiansPoolByRa || {};
  Object.keys(pools).forEach(ra => {
    (pools[ra] || []).forEach(g => {
      if (!g || !g.id || !g.name) return;
      const personId = `guardian:${g.id}`;
      if (personAlreadyListed(ata, g.name, personId)) return;
      const key = `guardian:${g.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        kind: "guardian",
        id: g.id,
        studentRa: ra,
        name: g.name,
        role: formatParticipantRole(g.role || "responsável"),
        responsibility: g.responsibility || null,
        relationship: g.relationship || null,
        personId
      });
    });
  });

  return out;
}

/**
 * Inclui participante na ata (staff ou responsável) para poder vincular falas.
 * Retorna patch + personId, ou null se inválido/já existir.
 */
export function addPersonToAta(ata, person) {
  if (!ata || !person || !String(person.name || "").trim()) return null;
  const name = String(person.name).trim();
  const role = formatParticipantRole(person.role || "participante");

  if (person.kind === "guardian" || person.studentRa) {
    const ra =
      person.studentRa ||
      (ata.students && ata.students[0] && ata.students[0].ra);
    if (!ra) return null;
    const existingId = person.id || null;
    const personId = existingId ? `guardian:${existingId}` : null;
    if (personAlreadyListed(ata, name, personId)) {
      const hit = listPersonOptions(ata).find(
        o =>
          o.id === personId ||
          normalizeNameKey(o.name) === normalizeNameKey(name)
      );
      return hit
        ? {
            students: ata.students,
            staff: ata.staff,
            personId: hit.id
          }
        : null;
    }
    const guardianId = existingId || `guest-guardian-${Date.now()}`;
    const students = (ata.students || []).map(s => {
      if (String(s.ra) !== String(ra)) return s;
      const guardians = [
        ...(s.guardians || []),
        {
          id: guardianId,
          name,
          role,
          responsibility:
            person.responsibility ||
            parseResponsibilityKey(person.relationship) ||
            parseResponsibilityKey(role) ||
            null,
          relationship: person.relationship || null,
          addedDuringReview: true
        }
      ];
      return {
        ...s,
        guardians,
        guardiansLabel: guardians.map(g => `${g.role}: ${g.name}`).join("; ")
      };
    });
    return {
      students,
      staff: ata.staff,
      personId: `guardian:${guardianId}`
    };
  }

  // staff / convidado da escola
  const staffId = person.id || `guest-staff-${Date.now()}`;
  const personId = `staff:${staffId}`;
  if (personAlreadyListed(ata, name, personId)) {
    const hit = listPersonOptions(ata).find(
      o =>
        o.id === personId || normalizeNameKey(o.name) === normalizeNameKey(name)
    );
    return hit
      ? { students: ata.students, staff: ata.staff, personId: hit.id }
      : null;
  }
  const staff = [
    ...(ata.staff || []),
    {
      id: staffId,
      name,
      role,
      addedDuringReview: true
    }
  ];
  return { students: ata.students, staff, personId };
}

/** Prefill a partir do nome detectado na fala (ex.: "Ana Coordenadora Infantil"). */
export function suggestGuestFromDetectedName(detectedName, detectedRole) {
  const raw = String(detectedName || "").trim();
  if (!raw || /^falante desconhecido/i.test(raw)) {
    return {
      name: "",
      role: formatParticipantRole(
        detectedRole || i18n.t("smartMinutes.role.coordination")
      )
    };
  }
  return {
    name: raw,
    role: formatParticipantRole(detectedRole || inferRoleFromSpeakerLabel(raw))
  };
}

function findPersonMatch(name, options) {
  const n = normalizeNameKey(name);
  if (!n) return null;
  const exact = options.find(o => normalizeNameKey(o.name) === n);
  if (exact) return exact;
  const tokens = n.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;

  const contains = options.filter(o => {
    const on = normalizeNameKey(o.name);
    return on.includes(n) || n.includes(on);
  });
  if (contains.length === 1) return contains[0];

  // Sobrenome + prenome: "roberta martins leite" ≈ "roberta leite"
  const last = tokens[tokens.length - 1];
  const first = tokens[0];
  const surnameHits = options.filter(o => {
    const ot = normalizeNameKey(o.name)
      .split(/\s+/)
      .filter(Boolean);
    if (ot.length < 2) return false;
    return ot[0] === first && ot[ot.length - 1] === last;
  });
  if (surnameHits.length === 1) return surnameHits[0];
  return contains[0] || null;
}

/**
 * Cabeçalho de turno: Speaker A/1 OU 2+ palavras capitalizadas (+ nº opcional).
 * Aceita "Nome (papel):" e também "Nome:" no início de linha (legado/STT).
 * Papel pode conter parênteses internos (ex.: Professor(a) — operador).
 */
const NAME_TOKEN =
  "(?:Speaker\\s+(?:\\d+|[A-Z])|[A-ZÀ-Ú][A-Za-zÀ-ÿ'-]*(?:\\s+[A-ZÀ-Ú][A-Za-zÀ-ÿ'-]*){1,7}(?:\\s+\\d+)?)";

const SPEAKER_HEADER_RE = new RegExp(
  `(${NAME_TOKEN})\\s*\\(([^:\\n]{2,80})\\):\\s*`
);

const SPEAKER_HEADER_BARE_RE = new RegExp(
  `(^|\\n)(\\s*)(${NAME_TOKEN})\\s*:\\s+`
);

function collectSpeakerHeaders(text) {
  const t = String(text || "");
  const matches = [];

  const withRole = new RegExp(SPEAKER_HEADER_RE.source, "g");
  let m = withRole.exec(t);
  while (m) {
    matches.push({
      index: m.index,
      headerLen: m[0].length,
      name: m[1].trim(),
      role: m[2].trim(),
      withRole: true
    });
    m = withRole.exec(t);
  }

  const bare = new RegExp(SPEAKER_HEADER_BARE_RE.source, "g");
  m = bare.exec(t);
  while (m) {
    const leadLen = m[1].length + m[2].length;
    const index = m.index + leadLen;
    const name = m[3].trim();
    const headerLen = m[0].length - leadLen;
    const overlapsRole = matches.some(
      h => h.withRole && index >= h.index && index < h.index + h.headerLen
    );
    // Evita "Nome (papel):" ser relido como bare no "Nome"
    const looksLikeRoleHeader = t
      .slice(index, index + headerLen + 2)
      .includes("(");
    if (!overlapsRole && !looksLikeRoleHeader) {
      matches.push({
        index,
        headerLen,
        name,
        role: inferRoleFromSpeakerLabel(name),
        withRole: false
      });
    }
    m = bare.exec(t);
  }

  matches.sort((a, b) => a.index - b.index || b.headerLen - a.headerLen);

  // Preferir cabeçalho com papel quando cobrem o mesmo ponto; remover aninhados
  const kept = [];
  matches.forEach(a => {
    const aEnd = a.index + a.headerLen;
    const dominated = kept.some(
      b => b.index <= a.index && b.index + b.headerLen >= aEnd
    );
    if (dominated) return;
    // Remove anteriores contidos neste
    for (let i = kept.length - 1; i >= 0; i -= 1) {
      const b = kept[i];
      const bEnd = b.index + b.headerLen;
      if (a.index <= b.index && aEnd >= bEnd) kept.splice(i, 1);
    }
    kept.push(a);
  });

  return kept.sort((a, b) => a.index - b.index);
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function firstNameOf(name) {
  return (
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)[0] || ""
  );
}

function uniqueNames(names) {
  const seen = new Set();
  const out = [];
  (names || []).forEach(n => {
    const raw = String(n || "").trim();
    const key = normalizeNameKey(raw);
    if (!raw || !key || seen.has(key)) return;
    seen.add(key);
    out.push(raw);
  });
  return out;
}

function textLooksSame(a, b) {
  const na = normalizeNameKey(a).replace(/[^\w\s]/g, " ");
  const nb = normalizeNameKey(b).replace(/[^\w\s]/g, " ");
  if (!na || !nb) return false;
  if (na === nb) return true;
  const short = na.length <= nb.length ? na : nb;
  const long = na.length <= nb.length ? nb : na;
  return short.length >= 40 && long.startsWith(short.slice(0, 80));
}

/**
 * Pessoa citada perto do fim do trecho (ex.: "chamei a Ana").
 * Preferência: prenome no final do texto, casando com roster/headers.
 */
function findIntroducedSpeaker(textBefore, speakerName, knownNames) {
  const tail = String(textBefore || "").slice(-220);
  const currentKey = normalizeNameKey(speakerName);
  let best = null;
  let bestPos = -1;

  uniqueNames(knownNames).forEach(fullName => {
    if (normalizeNameKey(fullName) === currentKey) return;
    const first = firstNameOf(fullName);
    if (!first || first.length < 2) return;
    // Evita casar "Alice" sozinha demais no meio da conversa sem sinal de introdução
    const re = new RegExp(
      `(?:chamei|chamo|chamar|com|para)\\s+(?:a|o|à|ao)?\\s*${escapeRegExp(
        first
      )}\\b|\\b${escapeRegExp(first)}\\s*,?\\s*da\\s+coordena`,
      "gi"
    );
    let m = re.exec(tail);
    while (m) {
      if (m.index >= bestPos) {
        bestPos = m.index;
        best = fullName;
      }
      m = re.exec(tail);
    }
  });

  return best;
}

/**
 * Detecta fala de outro participante colada no mesmo bloco (STT sem diarização).
 * Sinal forte: agradecer o falante atribuído ("Obrigada, Elisa…").
 * @returns {{ cutAt: number, nextName: string|null } | null}
 */
function findForeignSpeechCut(speakerName, body, knownNames) {
  const text = String(body || "");
  const first = firstNameOf(speakerName);
  if (!first || first.length < 2 || text.length < 80) return null;

  const thankRe = new RegExp(`Obrigad[oa],\\s*${escapeRegExp(first)}\\b`, "i");
  const thankMatch = thankRe.exec(text);
  if (!thankMatch) return null;

  let cutAt = thankMatch.index;

  // Inclui saudação imediatamente anterior ("Boa tarde a todos.")
  const before = text.slice(0, cutAt);
  const greetMatch = before.match(
    /(?:Boa\s+tarde|Bom\s+dia|Boa\s+noite)[^.!?]{0,60}[.!?…]?\s*$/i
  );
  if (greetMatch) {
    cutAt = before.length - greetMatch[0].length;
  }

  const partA = text.slice(0, cutAt).trim();
  const partB = text.slice(cutAt).trim();
  if (partA.length < 40 || partB.length < 24) return null;

  const nextName = findIntroducedSpeaker(partA, speakerName, knownNames);
  return { cutAt, nextName, partA, partB };
}

/**
 * Repara turnos em que o STT colou a fala seguinte no mesmo bloco.
 * Se o próximo turno já tiver o mesmo texto, só corta o atual.
 */
export function repairMergedSpeakerTurns(transcript, knownNames = []) {
  const raw = String(transcript || "")
    .trim()
    .replace(/\r\n/g, "\n");
  if (!raw) return raw;

  let working = raw;
  const namePool = uniqueNames([
    ...knownNames,
    ...collectSpeakerHeaders(working).map(h => h.name)
  ]);

  for (let pass = 0; pass < 6; pass += 1) {
    const snapshot = working;
    const headers = collectSpeakerHeaders(snapshot);
    if (!headers.length) return snapshot;

    let changed = false;
    const parts = [];
    const preamble = snapshot.slice(0, headers[0].index).trim();
    if (preamble) parts.push(preamble);

    for (let i = 0; i < headers.length; i += 1) {
      const h = headers[i];
      const end =
        i + 1 < headers.length ? headers[i + 1].index : snapshot.length;
      const body = snapshot.slice(h.index + h.headerLen, end).trim();
      const role = h.role || inferRoleFromSpeakerLabel(h.name);
      const cut = findForeignSpeechCut(h.name, body, namePool);

      if (!cut) {
        parts.push(`${h.name} (${role}): ${body}`.trim());
      } else {
        changed = true;
        parts.push(`${h.name} (${role}): ${cut.partA}`.trim());

        const nextHeader = i + 1 < headers.length ? headers[i + 1] : null;
        const nextBody = nextHeader
          ? snapshot
              .slice(
                nextHeader.index + nextHeader.headerLen,
                i + 2 < headers.length ? headers[i + 2].index : snapshot.length
              )
              .trim()
          : "";

        const alreadyNextTurn =
          nextHeader && textLooksSame(cut.partB, nextBody);

        // Se o próximo turno já é essa fala, só corta o bloco atual
        if (!alreadyNextTurn) {
          const nextName =
            cut.nextName || findIntroducedSpeaker(cut.partA, h.name, namePool);

          if (nextName) {
            if (
              !namePool.some(
                n => normalizeNameKey(n) === normalizeNameKey(nextName)
              )
            ) {
              namePool.push(nextName);
            }
            const nextRole = inferRoleFromSpeakerLabel(nextName);
            parts.push(`${nextName} (${nextRole}): ${cut.partB}`.trim());
          } else {
            parts.push(
              i18n
                .t("smartMinutes.role.unknownSpeaker", { name: cut.partB })
                .trim()
            );
          }
        }
      }
    }

    const next = parts.filter(Boolean).join("\n\n");
    if (!changed || next === snapshot) return next;
    working = next;
  }

  return working;
}

/**
 * Normaliza turnos Nome Completo (papel): — também converte "Nome:" legado
 * e tenta separar falas coladas no mesmo bloco (STT).
 * @param {string} text
 * @param {{ knownNames?: string[] }} [options]
 */
export function normalizeAttributedTranscript(text, options = {}) {
  const raw = String(text || "")
    .trim()
    .replace(/\r\n/g, "\n");
  if (!raw) return raw;

  const headers = collectSpeakerHeaders(raw);
  if (!headers.length) return raw;

  const parts = [];
  const preamble = raw.slice(0, headers[0].index).trim();
  if (preamble) parts.push(preamble);

  headers.forEach((h, i) => {
    const end = i + 1 < headers.length ? headers[i + 1].index : raw.length;
    const body = raw.slice(h.index + h.headerLen, end).trim();
    const role = h.role || inferRoleFromSpeakerLabel(h.name);
    parts.push(`${h.name} (${role}): ${body}`.trim());
  });

  const formatted = parts.filter(Boolean).join("\n\n");
  const knownNames = uniqueNames([
    ...(options.knownNames || []),
    ...headers.map(h => h.name)
  ]);
  return repairMergedSpeakerTurns(formatted, knownNames);
}

/** Trecho da fala no instante atual (legenda ao vivo). */
export function excerptTurnAtTime(turn, currentSec) {
  const text = String((turn && turn.text) || "").trim();
  if (!text) return "";
  if (text.length <= 240) return text;

  const start = Number.isFinite(turn.startSec) ? turn.startSec : 0;
  const end = Number.isFinite(turn.endSec) ? turn.endSec : start + 1;
  const span = Math.max(end - start, 0.001);
  const progress = Math.min(
    1,
    Math.max(0, ((Number(currentSec) || 0) - start) / span)
  );
  const window = 200;
  const center = Math.floor(progress * text.length);
  let from = Math.max(0, center - 36);
  let to = Math.min(text.length, from + window);
  if (to - from < window) from = Math.max(0, to - window);

  // Alinha em limite de palavra
  if (from > 0) {
    const space = text.indexOf(" ", from);
    if (space !== -1 && space < from + 24) from = space + 1;
  }
  if (to < text.length) {
    const space = text.lastIndexOf(" ", to);
    if (space > from + 40) to = space;
  }

  const slice = text.slice(from, to).trim();
  return `${from > 0 ? "…" : ""}${slice}${to < text.length ? "…" : ""}`;
}

/**
 * Quebra transcrição no formato "Nome Completo (papel): fala" em turnos.
 * @param {string} transcript
 * @param {{ knownNames?: string[] }} [options]
 */
export function parseTranscriptTurns(transcript, options = {}) {
  const text = normalizeAttributedTranscript(transcript, options);
  const headers = collectSpeakerHeaders(text);
  if (!headers.length) return [];

  return headers.map((h, i) => {
    const start = h.index + h.headerLen;
    const end = i + 1 < headers.length ? headers[i + 1].index : text.length;
    return {
      id: `turn_${i}`,
      name: h.name,
      role: h.role,
      text: text.slice(start, end).trim()
    };
  });
}

export function transcriptHasSpeakerTurns(transcript) {
  return parseTranscriptTurns(transcript).length >= 2;
}

function parseSpeakersFromTranscript(transcript) {
  const turns = parseTranscriptTurns(transcript);
  const detected = [];
  turns.forEach(t => {
    if (
      t.name &&
      !detected.some(d => d.name.toLowerCase() === t.name.toLowerCase())
    ) {
      detected.push({ name: t.name, role: t.role });
    }
  });
  return detected;
}

/** Localiza o falante da lista a partir do nome do turno na transcrição. */
export function findSpeakerForTurn(speakers, turnName) {
  const key = normalizeNameKey(turnName);
  if (!key) return null;
  const list = speakers || [];

  // Preferir nome detectado no STT (vínculo é metadado; personName pode divergir)
  const byDetected = list.find(s => normalizeNameKey(s.detectedName) === key);
  if (byDetected) return byDetected;

  // Legado: turnos já reescritos com o nome da pessoa validada
  const byPerson = list.find(s => normalizeNameKey(s.personName) === key);
  if (byPerson) return byPerson;

  return (
    list.find(s => {
      const n = normalizeNameKey(s.detectedName || s.label || s.personName);
      if (!n) return false;
      if (n.includes(key) || key.includes(n)) return true;
      const at = key.split(/\s+/);
      const bt = n.split(/\s+/);
      return (
        at.length >= 2 &&
        bt.length >= 2 &&
        at[0] === bt[0] &&
        at[at.length - 1] === bt[bt.length - 1]
      );
    }) || null
  );
}

/** Nome genérico do diarize (ainda sem pessoa da turma). */
export function isGenericSttName(name) {
  const n = normalizeNameKey(name);
  if (!n) return true;
  return (
    /^speaker(\s+[a-z0-9]+)?$/.test(n) ||
    /^falante(\s+\d+)?$/.test(n) ||
    /^participante(\s+\d+)?$/.test(n)
  );
}

/** Pessoa da ata cujo nome casa com o rótulo STT da fala. */
export function findPersonMatchingSttName(ata, sttName) {
  if (isGenericSttName(sttName)) return null;
  const key = normalizeNameKey(sttName);
  if (!key) return null;
  const options = listPersonOptions(ata);
  const exact = options.find(o => normalizeNameKey(o.name) === key);
  if (exact) return exact;
  return (
    options.find(o => {
      const n = normalizeNameKey(o.name);
      if (!n) return false;
      if (n.includes(key) || key.includes(n)) return true;
      const at = key.split(/\s+/);
      const bt = n.split(/\s+/);
      return (
        at.length >= 2 &&
        bt.length >= 2 &&
        at[0] === bt[0] &&
        at[at.length - 1] === bt[bt.length - 1]
      );
    }) || null
  );
}

/**
 * IDs reais cobertos por um turno (após merge de segmentos).
 */
export function expandTurnIds(turn) {
  if (!turn) return [];
  if (Array.isArray(turn.mergedTurnIds) && turn.mergedTurnIds.length) {
    return turn.mergedTurnIds.filter(Boolean);
  }
  return turn.id ? [turn.id] : [];
}

function memberIdsForTurn(turn) {
  const ids = expandTurnIds(turn);
  if (ids.length) return ids;
  return turn && turn.id ? [turn.id] : [];
}

export function getTurnLink(ata, turnId) {
  if (!ata || !turnId) return null;
  return (ata.turnLinks && ata.turnLinks[turnId]) || null;
}

export function computeVoicesLinkedFromTurns(transcriptText, turnLinks) {
  const turns = parseTranscriptTurns(transcriptText);
  if (!turns.length) return false;
  const links = turnLinks || {};
  return turns.every(
    t =>
      links[t.id] && links[t.id].personId && links[t.id].linkSource === "manual"
  );
}

export function applyTurnPersonToAta(ata, turn, personId) {
  if (!ata || !turn || !turn.id) return null;
  const options = listPersonOptions(ata);
  const person = personId ? options.find(o => o.id === personId) || null : null;
  const turnLinks = { ...(ata.turnLinks || {}) };

  if (!personId || !person) {
    const prev = turnLinks[turn.id];
    if (prev && (prev.suggestedPersonId || prev.suggestedPersonName)) {
      turnLinks[turn.id] = {
        ...prev,
        personId: null,
        personName: null,
        personRole: null,
        linkSource: "suggestion"
      };
    } else {
      delete turnLinks[turn.id];
    }
  } else {
    const prev = turnLinks[turn.id] || {};
    const responsibility =
      person.responsibility ||
      getPersonResponsibility(ata, person.id, {
        name: person.name,
        role: person.role
      });
    turnLinks[turn.id] = {
      ...prev,
      personId: person.id,
      personName: person.name,
      personRole: formatParticipantRole(person.role),
      personResponsibility: responsibility,
      suggestedPersonId: person.id,
      suggestedPersonName: person.name,
      suggestedPersonRole: formatParticipantRole(person.role),
      linkSource: "manual",
      sttName: turn.name,
      voiceSuggestScore: Number.isFinite(prev.voiceSuggestScore)
        ? prev.voiceSuggestScore
        : null
    };
  }

  const voicesLinked = computeVoicesLinkedFromTurns(
    ata.transcriptText,
    turnLinks
  );
  // eslint-disable-next-line no-use-before-define
  const speakers = syncSpeakerSamplesFromTurnLinks(
    ata.speakers,
    turnLinks,
    ata.transcriptText
  );
  return { turnLinks, voicesLinked, speakers };
}

function turnVoiceSuggestId(ata, turn) {
  if (!ata || !turn) return null;
  const ids = expandTurnIds(turn);
  for (let i = 0; i < ids.length; i += 1) {
    const link = getTurnLink(ata, ids[i]);
    if (link && link.suggestedPersonId && link.voiceSuggestSource !== "stt_name") {
      const score = Number(link.voiceSuggestScore);
      if (!Number.isFinite(score) || score >= 0.75) {
        return link.suggestedPersonId;
      }
    }
  }
  return null;
}

/**
 * Fronteira de voz: sugestão de voz forte muda de pessoa (ex.: Elisa → Ana).
 */
export function hasVoiceSpeakerBoundary(ata, turnA, turnB) {
  const a = turnVoiceSuggestId(ata, turnA);
  const b = turnVoiceSuggestId(ata, turnB);
  return !!(a && b && a !== b);
}

/**
 * Funde falas consecutivas do mesmo rótulo STT (diarize fragmenta demais).
 * Para se a sugestão de voz indicar outra pessoa com confiança.
 */
export function mergeConsecutiveSameSpeakerTurns(turns, ata, opts = {}) {
  const list = turns || [];
  if (list.length < 2) {
    return list.map(t => ({
      ...t,
      mergedTurnIds: memberIdsForTurn(t)
    }));
  }
  const maxGapSec = Number.isFinite(opts.maxGapSec) ? opts.maxGapSec : 1.25;
  const out = [];
  let cur = null;

  list.forEach(t => {
    const memberIds = memberIdsForTurn(t);
    if (!cur) {
      cur = { ...t, mergedTurnIds: memberIds };
      return;
    }
    const same =
      normalizeNameKey(cur.name) === normalizeNameKey(t.name) &&
      !isGenericSttName(cur.name);
    const gap =
      Number.isFinite(cur.endSec) && Number.isFinite(t.startSec)
        ? t.startSec - cur.endSec
        : 0;
    const canMerge =
      same &&
      gap <= maxGapSec &&
      !hasVoiceSpeakerBoundary(ata, cur, t);

    if (canMerge) {
      const left = String(cur.text || "").trim();
      const right = String(t.text || "").trim();
      cur = {
        ...cur,
        text: [left, right].filter(Boolean).join(" ").trim(),
        endSec: Number.isFinite(t.endSec) ? t.endSec : cur.endSec,
        mergedTurnIds: [...(cur.mergedTurnIds || []), ...memberIds],
        timeEstimated: cur.timeEstimated || t.timeEstimated,
        diarized: cur.diarized && t.diarized
      };
      return;
    }
    out.push(cur);
    cur = { ...t, mergedTurnIds: memberIds };
  });
  if (cur) out.push(cur);
  return out;
}

/**
 * Bloco a confirmar a partir da fala atual: mesmo STT à frente até
 * mudar o rótulo ou a sugestão de voz (fronteira Elisa→Ana).
 */
export function getConfirmSpeakerRun(turns, ata, fromTurn) {
  const list = turns || [];
  if (!fromTurn || !list.length) return null;
  const idx = list.findIndex(t => t.id === fromTurn.id);
  if (idx < 0) return null;
  const key = normalizeNameKey(fromTurn.name);
  if (!key) return null;

  const run = [list[idx]];
  for (let i = idx + 1; i < list.length; i += 1) {
    const next = list[i];
    if (normalizeNameKey(next.name) !== key) break;
    if (hasVoiceSpeakerBoundary(ata, run[run.length - 1], next)) break;
    run.push(next);
  }

  return {
    sttName: fromTurn.name,
    startIndex: idx,
    endIndex: idx + run.length - 1,
    run,
    count: run.length
  };
}

/** @deprecated use getConfirmSpeakerRun — lote à frente com fronteira de voz */
export function getSameSttTurnRun(turns, fromTurn) {
  return getConfirmSpeakerRun(turns, null, fromTurn);
}

export function listPendingTurnsInRun(ata, runTurns) {
  const pending = [];
  (runTurns || []).forEach(turn => {
    if (!ata || !turn) return;
    expandTurnIds(turn).forEach(id => {
      const link = getTurnLink(ata, id);
      const done = link && link.linkSource === "manual" && link.personId;
      if (!done) {
        pending.push({ ...turn, id, mergedTurnIds: [id] });
      }
    });
  });
  return pending;
}

/** Turnos de UI ainda pendentes no bloco (após merge). */
export function listPendingDisplayTurnsInRun(ata, runTurns) {
  return (runTurns || []).filter(turn => {
    if (!ata || !turn) return false;
    const ids = expandTurnIds(turn);
    if (!ids.length) {
      const link = getTurnLink(ata, turn.id);
      return !(link && link.linkSource === "manual" && link.personId);
    }
    return ids.some(id => {
      const link = getTurnLink(ata, id);
      return !(link && link.linkSource === "manual" && link.personId);
    });
  });
}

/**
 * Vincula várias falas à mesma pessoa (lote do mesmo STT / merge).
 */
export function applyTurnPersonToMany(ata, turns, personId) {
  if (!ata || !Array.isArray(turns) || !turns.length) return null;
  let next = ata;
  let lastPatch = null;
  turns.forEach(turn => {
    const patch = applyTurnPersonToAta(next, turn, personId);
    if (!patch) return;
    lastPatch = patch;
    next = { ...next, ...patch };
  });
  return lastPatch;
}

/**
 * Atualiza sugestão (sem confirmar) em falas ainda não validadas.
 */
export function applyTurnPersonSuggestionToMany(ata, turns, personId) {
  if (!ata || !personId || !Array.isArray(turns) || !turns.length) return null;
  const options = listPersonOptions(ata);
  const person = options.find(o => o.id === personId) || null;
  if (!person) return null;

  const turnLinks = { ...(ata.turnLinks || {}) };
  let changed = false;
  turns.forEach(turn => {
    expandTurnIds(turn).forEach(id => {
      const prev = turnLinks[id] || {};
      if (prev.linkSource === "manual" && prev.personId) return;
      changed = true;
      turnLinks[id] = {
        ...prev,
        suggestedPersonId: person.id,
        suggestedPersonName: person.name,
        suggestedPersonRole: formatParticipantRole(person.role),
        voiceSuggestSource: "user_link_propagate",
        voiceSuggestScore: Number.isFinite(prev.voiceSuggestScore)
          ? Math.max(prev.voiceSuggestScore, 0.9)
          : 0.9,
        sttName: (turn && turn.name) || prev.sttName || null,
        // Mantém não confirmado até o usuário validar
        personId: prev.linkSource === "manual" ? prev.personId : null,
        personName: prev.linkSource === "manual" ? prev.personName : null,
        linkSource:
          prev.linkSource === "manual" ? "manual" : "suggestion"
      };
    });
  });
  if (!changed) return null;

  const voicesLinked = computeVoicesLinkedFromTurns(
    ata.transcriptText,
    turnLinks
  );
  // eslint-disable-next-line no-use-before-define
  const speakers = syncSpeakerSamplesFromTurnLinks(
    ata.speakers,
    turnLinks,
    ata.transcriptText
  );
  return { turnLinks, voicesLinked, speakers };
}

/**
 * Ao escolher um participante numa fala:
 * 1) confirma o bloco consecutivo do mesmo STT (até fronteira de voz);
 * 2) atualiza a sugestão nas demais falas pendentes com o mesmo rótulo STT.
 * Assim a correção “puxa” as falas ainda não confirmadas do mesmo falante.
 *
 * @returns {{ patch, confirmedTurns, suggestedTurns } | null}
 */
export function applyPersonSelectionToPendingTurns(
  ata,
  fromTurn,
  personId,
  allTurns
) {
  if (!ata || !fromTurn || !personId) return null;
  const turns =
    Array.isArray(allTurns) && allTurns.length
      ? allTurns
      : mergeConsecutiveSameSpeakerTurns(
          parseTranscriptTurns(ata.transcriptText || ""),
          ata
        );
  const runInfo = getConfirmSpeakerRun(turns, ata, fromTurn);
  const run = (runInfo && runInfo.run) || [fromTurn];
  let toConfirm = listPendingTurnsInRun(ata, run);
  // Sempre inclui a fala atual (troca de participante já confirmado)
  const fromIds = new Set(expandTurnIds(fromTurn));
  const coversFrom = toConfirm.some(t =>
    expandTurnIds(t).some(id => fromIds.has(id))
  );
  if (!coversFrom) {
    toConfirm = [
      ...expandTurnIds(fromTurn).map(id => ({
        ...fromTurn,
        id,
        mergedTurnIds: [id]
      })),
      ...toConfirm
    ];
  }

  let next = ata;
  let lastPatch = null;
  if (toConfirm.length) {
    lastPatch = applyTurnPersonToMany(next, toConfirm, personId);
    if (lastPatch) next = { ...next, ...lastPatch };
  } else {
    lastPatch = applyTurnPersonToAta(next, fromTurn, personId);
    if (lastPatch) next = { ...next, ...lastPatch };
  }

  const sttKey = normalizeNameKey(fromTurn.name);
  const alreadyConfirmedIds = new Set(
    toConfirm.flatMap(t => expandTurnIds(t))
  );

  const toSuggest = [];
  turns.forEach(t => {
    if (!t || normalizeNameKey(t.name) !== sttKey) return;
    if (isGenericSttName(t.name)) return;
    const ids = expandTurnIds(t);
    const pendingIds = ids.filter(id => {
      if (alreadyConfirmedIds.has(id)) return false;
      const link = getTurnLink(next, id);
      return !(link && link.linkSource === "manual" && link.personId);
    });
    if (!pendingIds.length) return;
    toSuggest.push(t);
  });

  if (toSuggest.length) {
    const suggestPatch = applyTurnPersonSuggestionToMany(
      next,
      toSuggest,
      personId
    );
    if (suggestPatch) {
      lastPatch = suggestPatch;
      next = { ...next, ...suggestPatch };
    }
  }

  return lastPatch
    ? {
        patch: lastPatch,
        confirmedTurns: toConfirm.length ? toConfirm : [fromTurn],
        suggestedTurns: toSuggest
      }
    : null;
}

/** Nome/papel para UI por fala: vínculo da fala > STT casado > sugestão de voz > STT. */
export function displaySpeakerForTurn(turn, speaker, turnLink, ata) {
  const sttName = (turn && turn.name) || "";
  const sttRole = (turn && turn.role) || null;
  const link = turnLink || null;
  // Preferir o melhor score disponível: confirmação não deve “derrubar”
  // um percentual alto da sugestão (bug do lab/API com match baixo).
  const scores = [
    link && link.matchConfidence,
    link && link.voiceSuggestScore,
    speaker && speaker.voiceSuggestScore
  ].filter(n => Number.isFinite(n));
  const confidence = scores.length ? Math.max(...scores) : null;
  const confidenceSource =
    (link && link.voiceSuggestSource) ||
    (speaker && speaker.voiceSuggestSource) ||
    null;

  const clusterLabel =
    (speaker && speaker.label) || (speaker && speaker.participantLabel) || null;
  const voiceDescriptor = speaker
    ? resolveSpeakerVoiceDescriptor(speaker)
    : null;

  if (link && link.linkSource === "manual" && link.personId) {
    const responsibility =
      link.personResponsibility ||
      getPersonResponsibility(ata, link.personId, {
        name: link.personName || sttName,
        role: link.personRole || sttRole
      });
    return {
      name: link.personName || sttName,
      role: formatParticipantRole(link.personRole || sttRole),
      responsibility,
      voiceDescriptor,
      validated: true,
      suggested: false,
      personId: link.personId,
      suggestedPersonId: link.personId,
      suggestedPersonName: link.personName,
      confidence,
      confidenceSource
    };
  }

  const options = listPersonOptions(ata);
  const sttMatch = findPersonMatchingSttName(ata, sttName);
  const voiceSuggestedId =
    (link && link.suggestedPersonId) ||
    (speaker && speaker.suggestedPersonId) ||
    null;

  // Nome STT casa com alguém da ata: prevalece sobre sugestão de voz conflitante
  // (evita Elisa no áudio com seletor em Emanuel/Roberta por match de voz ruidoso).
  let suggestedId = voiceSuggestedId;
  let suggestedFromStt = false;
  if (sttMatch) {
    if (!voiceSuggestedId || voiceSuggestedId === sttMatch.id) {
      suggestedId = sttMatch.id;
      suggestedFromStt = true;
    } else if (!isGenericSttName(sttName)) {
      suggestedId = sttMatch.id;
      suggestedFromStt = true;
    }
  }

  const suggestedFromOptions =
    suggestedId && options.find(o => o.id === suggestedId);
  const suggestedName =
    (suggestedFromStt && sttMatch && sttMatch.name) ||
    (link && link.suggestedPersonName) ||
    (speaker && speaker.suggestedPersonName) ||
    (suggestedFromOptions && suggestedFromOptions.name) ||
    null;
  const suggestedRole =
    (suggestedFromStt && sttMatch && sttMatch.role) ||
    (link && link.suggestedPersonRole) ||
    (speaker && speaker.suggestedPersonRole) ||
    (suggestedFromOptions && suggestedFromOptions.role) ||
    sttRole ||
    (speaker && speaker.detectedRole);

  const anonName =
    clusterLabel || sttName || i18n.t("smartMinutes.participant");

  if (suggestedId) {
    const responsibility =
      (suggestedFromStt && sttMatch && sttMatch.responsibility) ||
      (link && link.personResponsibility) ||
      (suggestedFromOptions && suggestedFromOptions.responsibility) ||
      getPersonResponsibility(ata, suggestedId, {
        name: suggestedName || sttName,
        role: suggestedRole
      });
    // Topo = sugestão efetiva (STT casado ou voz), alinhada ao seletor
    return {
      name: suggestedName || anonName,
      role: formatParticipantRole(suggestedRole),
      responsibility,
      voiceDescriptor: suggestedFromStt ? null : voiceDescriptor,
      validated: false,
      suggested: true,
      personId: null,
      suggestedPersonId: suggestedId,
      suggestedPersonName: suggestedName || anonName,
      confidence: suggestedFromStt ? null : confidence,
      confidenceSource: suggestedFromStt ? null : confidenceSource,
      suggestedFromStt
    };
  }

  const responsibility = getPersonResponsibility(ata, null, {
    name: sttName,
    role: sttRole || (speaker && speaker.detectedRole)
  });

  return {
    name: anonName,
    role: formatParticipantRole(sttRole || (speaker && speaker.detectedRole)),
    responsibility,
    voiceDescriptor,
    validated: false,
    suggested: false,
    personId: null,
    suggestedPersonId: null,
    suggestedPersonName: null,
    confidence: null,
    confidenceSource: null
  };
}

/** Ex.: 0.87 → "87%" */
export function formatVoiceConfidence(score) {
  if (!Number.isFinite(score)) return null;
  const pct = Math.round(Math.max(0, Math.min(1, score)) * 100);
  return `${pct}%`;
}

/** Cores do percentual de confiança (texto + fundo). */
export function voiceConfidenceStyle(score) {
  if (!Number.isFinite(score)) {
    return { color: "#546e7a", background: "#eceff1" };
  }
  if (score >= 0.8) {
    return { color: "#1b5e20", background: "#c8e6c9" };
  }
  if (score >= 0.6) {
    return { color: "#e65100", background: "#ffe0b2" };
  }
  return { color: "#b71c1c", background: "#ffcdd2" };
}

/** Aplica previsões de voz em falas ainda não validadas. */
export function applyTurnVoiceSuggestions(ata, suggestions) {
  const turnLinks = { ...(ata.turnLinks || {}) };
  let changed = false;
  const turnsById = {};
  parseTranscriptTurns(ata.transcriptText || "").forEach(t => {
    turnsById[t.id] = t;
  });
  (suggestions || []).forEach(s => {
    if (!s || !s.turnId || !s.personId) return;
    const prev = turnLinks[s.turnId];
    if (prev && prev.linkSource === "manual") return;
    const turn = turnsById[s.turnId];
    const sttName = (turn && turn.name) || (prev && prev.sttName) || s.sttName;
    const sttMatch = findPersonMatchingSttName(ata, sttName);
    // Não sobrescrever identidade STT clara com match de voz conflitante
    if (
      sttMatch &&
      sttMatch.id !== s.personId &&
      !isGenericSttName(sttName)
    ) {
      if (!prev || prev.linkSource !== "manual") {
        changed = true;
        turnLinks[s.turnId] = {
          ...(prev || {}),
          personId: null,
          personName: null,
          personRole: null,
          suggestedPersonId: sttMatch.id,
          suggestedPersonName: sttMatch.name,
          suggestedPersonRole: formatParticipantRole(sttMatch.role),
          linkSource: "suggestion",
          voiceSuggestScore: null,
          voiceSuggestSource: "stt_name",
          sttName: sttName || null
        };
      }
      return;
    }
    changed = true;
    let alternates = [];
    if (Array.isArray(s.alternates)) {
      alternates = s.alternates
        .filter(
          a =>
            a &&
            a.personId &&
            a.personId !== s.personId &&
            Number.isFinite(Number(a.score))
        )
        .map(a => ({
          personId: a.personId,
          personName: a.personName || a.displayName || null,
          score: Number(a.score)
        }));
    } else if (prev && Array.isArray(prev.voiceSuggestAlternates)) {
      alternates = prev.voiceSuggestAlternates;
    }
    turnLinks[s.turnId] = {
      ...(prev || {}),
      personId: null,
      personName: null,
      personRole: null,
      suggestedPersonId: s.personId,
      suggestedPersonName: s.personName || s.displayName || null,
      suggestedPersonRole: formatParticipantRole(s.personRole || s.role),
      linkSource: "suggestion",
      voiceSuggestScore: Number(s.score) || null,
      voiceSuggestSource: s.scoreSource || prev.voiceSuggestSource || null,
      voiceSuggestAlternates: alternates,
      sttName: (prev && prev.sttName) || s.sttName || null
    };
  });
  if (!changed) return null;
  return { turnLinks };
}

/**
 * Remove cabeçalhos "Nome (papel):" que vazaram para o corpo da fala
 * (efeito colateral do rewrite antigo). Só para exibição.
 * Papel pode conter parênteses (ex.: Professor(a) — operador).
 */
export function cleanTurnBodyForDisplay(text) {
  const raw = String(text || "");
  if (!raw.trim()) return raw;
  // Papel: qualquer coisa até "):" (permite ) internos no rótulo)
  const rolePart = "\\([^:]{2,80}\\):\\s*";
  const headerRe = new RegExp(
    `(?:^|[\\n\\r])\\s*${NAME_TOKEN}\\s*${rolePart}`,
    "g"
  );
  const inlineRe = new RegExp(`${NAME_TOKEN}\\s*${rolePart}`, "g");
  let out = raw.replace(headerRe, "\n");
  out = out.replace(inlineRe, "");
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

/** Quantidade de falas na transcrição para um falante. */
export function countTurnsForSpeaker(transcriptText, speaker) {
  const key = normalizeNameKey(
    (speaker &&
      (speaker.personName || speaker.detectedName || speaker.label)) ||
      ""
  );
  if (!key) return 0;
  return parseTranscriptTurns(transcriptText).filter(t => {
    const n = normalizeNameKey(t.name);
    if (!n) return false;
    if (n === key) return true;
    const at = key.split(/\s+/);
    const bt = n.split(/\s+/);
    return (
      at.length >= 2 &&
      bt.length >= 2 &&
      at[0] === bt[0] &&
      at[at.length - 1] === bt[bt.length - 1]
    );
  }).length;
}

/** Mínimo de amostras de fala para considerar o falante “identificado” na voz. */
export const MIN_VOICE_SAMPLES = 3;

/**
 * Score mínimo para pré-selecionar interlocutor no seletor (sem confirmar).
 */
export const PRESELECT_VOICE_SCORE = 0.8;

/** @deprecated use PRESELECT_VOICE_SCORE — auto-confirm removido do fluxo. */
export const AUTO_CONFIRM_VOICE_SCORE = PRESELECT_VOICE_SCORE;

/** Meta discreta sob o nome: "Pai · Resp. financeiro" */
export function formatSpeakerMetaLine({ role, responsibility } = {}) {
  const parts = [];
  const roleLabel = formatParticipantRole(role);
  if (roleLabel && roleLabel !== i18n.t("smartMinutes.role.participantLower"))
    parts.push(roleLabel);
  const resp = formatResponsibilityLabel(responsibility);
  if (resp) parts.push(resp);
  return parts.join(" · ");
}

export function participantLabelForIndex(index) {
  return i18n.t("smartMinutes.role.participantN", { n: Number(index) + 1 });
}

function voiceGenderToken(descriptor) {
  const d = normalizeNameKey(descriptor);
  if (/voz infantil/.test(d)) return "child";
  if (/voz masculina/.test(d)) return "male";
  if (/voz feminina/.test(d)) return "female";
  return null;
}

/** Descritor atual conflita com o esperado para a pessoa? */
export function voiceDescriptorConflicts(current, expected) {
  if (!current || !expected) return false;
  const a = voiceGenderToken(current);
  const b = voiceGenderToken(expected);
  return !!(a && b && a !== b);
}

/**
 * Ao confirmar fala: ajusta timbre/tom do cluster se ainda não foi
 * editado manualmente (ou se conflita com o perfil da pessoa).
 */
export function applyVoiceTraitsFromConfirm(ata, turn, person) {
  if (!ata || !turn || !person) return null;
  const speakers = ata.speakers || [];
  if (!speakers.length) return null;
  const turnKey = normalizeNameKey(turn.name);
  const hint = inferVoiceDescriptorHint({
    name: person.name,
    role: person.role,
    responsibility: person.responsibility
  });
  if (!hint) return null;

  let changed = false;
  const next = speakers.map(s => {
    const sttKey = normalizeNameKey(s.detectedName || s.label || "");
    const matchStt = turnKey && sttKey && turnKey === sttKey;
    const matchPerson =
      (s.personId && s.personId === person.id) ||
      (s.suggestedPersonId && s.suggestedPersonId === person.id);
    if (!matchStt && !matchPerson) return s;
    if (s.voiceDescriptorSource === "manual") return s;
    const current = s.voiceDescriptor || "";
    const conflict = voiceDescriptorConflicts(current, hint);
    if (current === hint && s.voiceDescriptorSource === "person_confirm") {
      return s;
    }
    if (!conflict && current && s.voiceDescriptorSource === "person_confirm") {
      return s;
    }
    // Sempre corrige conflito (ex.: mãe com "voz masculina")
    if (!conflict && current && !s.voiceDescriptorSource) {
      // índice mock inicial — sobrescreve com hint da pessoa
    }
    changed = true;
    return {
      ...s,
      voiceDescriptor: hint,
      voiceDescriptorSource: "person_confirm"
    };
  });
  return changed ? { speakers: next } : null;
}

/** Score mínimo para considerar match alternativo de voz (cards ainda vazios). */
export const ALT_VOICE_SCORE = 0.55;

function turnToSample(t) {
  return {
    id: t.id,
    turnId: t.id,
    startSec: t.startSec,
    endSec: t.endSec,
    estimated: !!t.estimated || !!t.timeEstimated
  };
}

/**
 * Falas para revisão no card do participante.
 * Inclui STT do cluster e falas cuja sugestão de voz aponta para esta pessoa
 * (mesmo com nome STT de outro). Com sugestão forte para outra pessoa, a fala
 * sai do card STT e vai para o card sugerido — alimenta cards ainda vazios.
 */
export function listSpeakerAudioSamples(speaker, turns, turnLinks) {
  if (!speaker) return [];
  const excluded = new Set(speaker.excludedSampleIds || []);
  const sttKey = normalizeNameKey(speaker.detectedName || speaker.label || "");
  const personId = speaker.personId || speaker.suggestedPersonId || null;
  const links = turnLinks || {};

  return (turns || [])
    .filter(t => {
      if (!t || excluded.has(t.id)) return false;
      const link = links[t.id];
      const suggestId = link && link.suggestedPersonId;
      const score = link && Number(link.voiceSuggestScore);
      const strongSuggest =
        suggestId && Number.isFinite(score) && score >= PRESELECT_VOICE_SCORE;

      if (personId && suggestId === personId) return true;
      if (
        personId &&
        link &&
        link.linkSource === "manual" &&
        link.personId === personId
      ) {
        return true;
      }

      const mineByStt = sttKey && normalizeNameKey(t.name) === sttKey;
      if (!mineByStt) return false;
      // Sugestão forte para outra pessoa: redistribute para o outro card
      if (strongSuggest && suggestId !== personId) return false;
      return true;
    })
    .map(turnToSample);
}

/**
 * Pendências por participante (exclusivas). Cards vazios recebem falas em que
 * a pessoa aparece como match alternativo de voz (confiança secundária).
 */
export function listPendingSamplesBySpeaker(speakers, turns, turnLinks) {
  const links = turnLinks || {};
  const list = speakers || [];
  const isPendingTurn = t => {
    const link = links[t.id];
    return !(link && link.linkSource === "manual" && link.personId);
  };

  const result = {};
  const claimed = new Set();

  list.forEach(sp => {
    const samples = listSpeakerAudioSamples(sp, turns, links).filter(s =>
      isPendingTurn({ id: s.turnId || s.id })
    );
    result[sp.id] = samples;
    samples.forEach(s => claimed.add(s.id));
  });

  list.forEach(sp => {
    if ((result[sp.id] || []).length) return;
    const personId = sp.personId || sp.suggestedPersonId;
    if (!personId) return;
    const excluded = new Set(sp.excludedSampleIds || []);
    const extras = [];
    (turns || []).forEach(t => {
      if (!t || claimed.has(t.id) || excluded.has(t.id)) return;
      if (!isPendingTurn(t)) return;
      const link = links[t.id];
      const alts = (link && link.voiceSuggestAlternates) || [];
      const hit = alts.find(
        a => a && a.personId === personId && Number(a.score) >= ALT_VOICE_SCORE
      );
      if (!hit) return;
      extras.push(turnToSample(t));
      claimed.add(t.id);
    });
    result[sp.id] = extras;
  });

  return result;
}

/**
 * Amostras = falas confirmadas (manual) deste participante.
 * Conta por personId quando houver; senão por nome STT do falante.
 */
export function countConfirmedSamplesForSpeaker(
  speaker,
  turnLinks,
  transcriptText
) {
  if (!speaker) return 0;
  const links = turnLinks || {};
  const turns = parseTranscriptTurns(transcriptText);
  const personId = speaker.personId || null;
  const sttKey = normalizeNameKey(speaker.detectedName || speaker.label || "");

  return turns.filter(t => {
    const link = links[t.id];
    if (!link || link.linkSource !== "manual" || !link.personId) return false;
    if (personId && link.personId === personId) return true;
    if (!personId && sttKey && normalizeNameKey(t.name) === sttKey) {
      return true;
    }
    // Falante ainda sem personId, mas já sugerido: conta confirmações desse person
    if (
      !personId &&
      speaker.suggestedPersonId &&
      link.personId === speaker.suggestedPersonId
    ) {
      return true;
    }
    return false;
  }).length;
}

/** Falas deste falante ainda sem confirmação manual. */
export function countPendingTurnsForSpeaker(
  speaker,
  turnLinks,
  transcriptText
) {
  if (!speaker) return 0;
  const links = turnLinks || {};
  const turns = parseTranscriptTurns(transcriptText);
  const personId = speaker.personId || speaker.suggestedPersonId || null;
  const sttKey = normalizeNameKey(speaker.detectedName || speaker.label || "");

  return turns.filter(t => {
    const link = links[t.id];
    if (link && link.linkSource === "manual" && link.personId) return false;

    const suggestId = link && link.suggestedPersonId;
    const score = link && Number(link.voiceSuggestScore);
    const strongSuggest =
      suggestId && Number.isFinite(score) && score >= PRESELECT_VOICE_SCORE;

    if (personId && suggestId === personId) return true;

    const mineByStt = sttKey && normalizeNameKey(t.name) === sttKey;
    if (mineByStt && !(strongSuggest && suggestId !== personId)) return true;
    return false;
  }).length;
}

export function getSpeakerSampleCount(speaker, transcriptText, turnLinks) {
  if (turnLinks) {
    return countConfirmedSamplesForSpeaker(speaker, turnLinks, transcriptText);
  }
  if (Number.isFinite(speaker && speaker.confirmedSampleCount)) {
    return speaker.confirmedSampleCount;
  }
  if (Number.isFinite(speaker && speaker.sampleCount)) {
    return speaker.sampleCount;
  }
  const fromSamples = Array.isArray(speaker && speaker.voiceSamples)
    ? speaker.voiceSamples.length
    : 0;
  if (fromSamples > 0) return fromSamples;
  return countTurnsForSpeaker(transcriptText, speaker);
}

export function syncSpeakerSamplesFromTurnLinks(
  speakers,
  turnLinks,
  transcriptText
) {
  return (speakers || []).map(s => {
    const sampleCount = countConfirmedSamplesForSpeaker(
      s,
      turnLinks,
      transcriptText
    );
    let voiceCapture = "insufficient";
    if (sampleCount >= MIN_VOICE_SAMPLES) voiceCapture = "ready";
    else if (sampleCount > 0) voiceCapture = "partial";
    return {
      ...s,
      sampleCount,
      confirmedSampleCount: sampleCount,
      voiceCapture
    };
  });
}

export function countSpeakersWithMinSamples(
  speakers,
  transcriptText,
  min = MIN_VOICE_SAMPLES,
  turnLinks
) {
  return (speakers || []).filter(
    s => getSpeakerSampleCount(s, transcriptText, turnLinks) >= min
  ).length;
}

/**
 * Clusters + descritores + amostras (timestamps de diarização quando houver).
 */
export function attachMockVoiceSamples(
  speakers,
  transcriptText,
  diarizedSegments
) {
  let turns = parseTranscriptTurns(transcriptText);
  // eslint-disable-next-line no-use-before-define
  const withTimes = attachDiarizedTimestamps(turns, diarizedSegments);
  if (withTimes) turns = withTimes;
  return (speakers || []).map((s, index) => {
    const label = s.label || participantLabelForIndex(index);
    const inferred = inferVoiceDescriptorHint({
      name: s.personName || s.suggestedPersonName || s.label || s.detectedName,
      role: s.personRole || s.suggestedPersonRole || s.detectedRole
    });
    const {
      voiceDescriptor: prevDescriptor,
      voiceDescriptorSource: prevSource
    } = s;
    const locked = prevSource === "manual" || prevSource === "person_confirm";
    let voiceDescriptor = prevDescriptor;
    let voiceDescriptorSource = prevSource || null;
    if (!locked) {
      if (inferred) {
        // Atualiza pré-seleção (corrige índice/legado "infantil" em cargo escolar)
        voiceDescriptor = inferred;
        voiceDescriptorSource = "inferred";
      } else if (
        !voiceDescriptor ||
        prevSource === "index" ||
        /voz infantil/i.test(String(voiceDescriptor || ""))
      ) {
        voiceDescriptor = voiceDescriptorForIndex(index);
        voiceDescriptorSource = "index";
      }
    }
    const sttKey = normalizeNameKey(s.detectedName || s.label || "");
    const mine = turns.filter(t => normalizeNameKey(t.name) === sttKey);
    const excluded = new Set(s.excludedSampleIds || []);
    const voiceSamples = mine.slice(0, 8).map((t, i) => {
      const realTime =
        t.diarized === true &&
        Number.isFinite(t.startSec) &&
        Number.isFinite(t.endSec);
      return {
        id: t.id || `smp_${index}_${i}`,
        turnId: t.id,
        startSec: Number.isFinite(t.startSec) ? t.startSec : null,
        endSec: Number.isFinite(t.endSec) ? t.endSec : null,
        estimated: !realTime,
        excluded: excluded.has(t.id)
      };
    });
    const sampleCount = voiceSamples.filter(v => !v.excluded).length;
    const hasDiarizeTimes = voiceSamples.some(
      v =>
        !v.excluded &&
        Number.isFinite(v.startSec) &&
        Number.isFinite(v.endSec) &&
        v.estimated === false
    );
    // Com diarização: timestamps reais; senão proxy por turnos STT
    let voiceCapture = hasDiarizeTimes
      ? "diarize_insufficient"
      : "turn_proxy_insufficient";
    if (sampleCount >= MIN_VOICE_SAMPLES) {
      voiceCapture = hasDiarizeTimes ? "diarize_ready" : "turn_proxy_ready";
    } else if (sampleCount > 0) {
      voiceCapture = hasDiarizeTimes ? "diarize_partial" : "turn_proxy_partial";
    }
    const first = voiceSamples.find(v => !v.excluded);

    return {
      ...s,
      label,
      participantLabel: label,
      voiceDescriptor,
      voiceDescriptorSource,
      sampleCount,
      voiceSamples,
      voiceCapture,
      sampleEstimated: !hasDiarizeTimes,
      sampleStartSec: first ? first.startSec : null,
      sampleEndSec: first ? first.endSec : null
    };
  });
}

export function formatTurnTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return null;
  const total = Math.floor(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Turno ativo no instante do áudio (timestamps estimados ou reais). */
export function findActiveTurn(turns, currentSec) {
  const list = turns || [];
  if (!list.length || !Number.isFinite(currentSec)) return null;
  const t = Math.max(0, currentSec);
  const inRange = list.find(
    turn =>
      Number.isFinite(turn.startSec) &&
      Number.isFinite(turn.endSec) &&
      t >= turn.startSec &&
      t < turn.endSec
  );
  if (inRange) return inRange;
  let active = null;
  for (let i = 0; i < list.length; i += 1) {
    const turn = list[i];
    if (!Number.isFinite(turn.startSec) || turn.startSec > t) break;
    active = turn;
  }
  return active || list[0];
}

/**
 * Normaliza texto de fala para casar turno ↔ segmento de diarização.
 */
function normalizeTurnTextKey(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function scoreTextMatch(a, b) {
  const left = normalizeTurnTextKey(a);
  const right = normalizeTurnTextKey(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) {
    const shorter = Math.min(left.length, right.length);
    const longer = Math.max(left.length, right.length);
    return 0.7 + (0.25 * shorter) / longer;
  }
  const at = left.split(" ").filter(w => w.length > 2);
  const bt = right.split(" ").filter(w => w.length > 2);
  if (!at.length || !bt.length) return 0;
  const setA = new Set(at);
  const hit = bt.filter(w => setA.has(w)).length;
  return hit / Math.max(at.length, bt.length);
}

function firstFiniteNumber(...values) {
  for (let i = 0; i < values.length; i += 1) {
    if (Number.isFinite(values[i])) return Number(values[i]);
  }
  return null;
}

function readSegmentBounds(seg) {
  if (!seg) return null;
  const start = firstFiniteNumber(seg.start, seg.startSec);
  const end = firstFiniteNumber(seg.end, seg.endSec);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end, text: seg.text };
}

/**
 * Aplica timestamps reais da diarização (gpt-4o-transcribe-diarize) aos turnos.
 * 1) Se contagens iguais e textos batem → casa por índice.
 * 2) Senão → casa por similaridade de texto (evita áudio de outra fala).
 */
export function attachDiarizedTimestamps(turns, segments) {
  const list = turns || [];
  const segs = Array.isArray(segments) ? segments : [];
  if (!list.length || !segs.length) return null;

  const bounds = segs.map(readSegmentBounds);
  const usable = bounds.filter(Boolean);
  if (!usable.length) return null;

  const indexAligned =
    list.length === segs.length &&
    list.length === usable.length &&
    list.slice(0, Math.min(5, list.length)).every((t, i) => {
      const b = bounds[i];
      if (!b) return false;
      // Sem texto no segmento: confia no índice
      if (!normalizeTurnTextKey(b.text)) return true;
      return scoreTextMatch(t.text, b.text) >= 0.4;
    });

  if (indexAligned) {
    return list.map((t, i) => {
      const b = bounds[i];
      if (!b) return t;
      return {
        ...t,
        startSec: b.start,
        endSec: b.end,
        timeEstimated: false,
        diarized: true
      };
    });
  }

  // Contagem divergiu ou índice desalinhado: casar por texto
  const used = new Set();
  let matched = 0;
  const next = list.map(t => {
    let bestIdx = -1;
    let bestScore = 0;
    bounds.forEach((b, i) => {
      if (!b || used.has(i)) return;
      const score = scoreTextMatch(t.text, b.text);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    });
    if (bestIdx >= 0 && bestScore >= 0.4) {
      used.add(bestIdx);
      matched += 1;
      const b = bounds[bestIdx];
      return {
        ...t,
        startSec: b.start,
        endSec: b.end,
        timeEstimated: false,
        diarized: true
      };
    }
    return t;
  });

  // Se quase nada casou, não inventa sync — deixa estimativa
  if (matched < Math.max(1, Math.floor(list.length * 0.35))) {
    return null;
  }
  return next;
}

/**
 * Estima início/fim de cada turno pela duração do áudio e tamanho do texto.
 * Fallback quando não há diarização com timestamps.
 */
export function attachEstimatedTimestamps(turns, durationSec) {
  const list = turns || [];
  if (!list.length) return [];
  const duration =
    Number.isFinite(durationSec) && durationSec > 0 ? durationSec : null;
  if (!duration) {
    // Fallback: ~8s por turno se não houver áudio
    let cursor = 0;
    return list.map(t => {
      const startSec = cursor;
      const endSec = cursor + 8;
      cursor = endSec;
      return { ...t, startSec, endSec, timeEstimated: true };
    });
  }
  const weights = list.map(t => Math.max(String(t.text || "").length, 24));
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  let cursor = 0;
  return list.map((t, i) => {
    const startSec = cursor;
    const slice = (weights[i] / total) * duration;
    cursor += slice;
    const endSec = i === list.length - 1 ? duration : cursor;
    return { ...t, startSec, endSec, timeEstimated: true };
  });
}

function speakersFromParticipants(ata) {
  const options = listPersonOptions(ata);
  const fromPresent = [];
  (ata.students || []).forEach(s => {
    if (s.present === true) {
      fromPresent.push({
        name: s.name,
        role: formatParticipantRole("aluno"),
        personId: `student:${s.ra}`
      });
    }
    (s.guardians || []).forEach(g => {
      fromPresent.push({
        name: g.name,
        role: formatParticipantRole(g.role || "responsável"),
        personId: `guardian:${g.id}`
      });
    });
  });
  (ata.staff || []).forEach(s => {
    fromPresent.push({
      name: s.name,
      role: formatParticipantRole(s.role || "funcionário"),
      personId: `staff:${s.id}`
    });
  });
  if (fromPresent.length) return fromPresent;
  return options.map(o => ({
    name: o.name,
    role: formatParticipantRole(o.role),
    personId: o.id
  }));
}

/**
 * Match automático = previsão. Só linkSource "manual" confirma o vínculo.
 */
function toSpeakerRow(d, index, options) {
  const manual =
    d.linkSource === "manual" && d.personId
      ? options.find(o => o.id === d.personId) || {
          id: d.personId,
          name: d.personName,
          role: d.personRole
        }
      : null;
  const suggested =
    manual ||
    (d.suggestedPersonId && options.find(o => o.id === d.suggestedPersonId)) ||
    (d.personId &&
      d.linkSource !== "manual" &&
      options.find(o => o.id === d.personId)) ||
    findPersonMatch(d.name, options) ||
    null;

  const detectedFrom = d.detectedFrom || "roster";
  const isNew = detectedFrom === "transcript" && !suggested;
  const hasRealSample =
    Number.isFinite(d.sampleStartSec) && Number.isFinite(d.sampleEndSec);

  const detectedName =
    d.name ||
    d.detectedName ||
    i18n.t("smartMinutes.role.participantSpace", {
      n: index + 1
    });
  const detectedRole = formatParticipantRole(
    d.role || d.detectedRole || (suggested && suggested.role) || null
  );

  if (manual) {
    return {
      id: d.id || `spk_${index}`,
      label: manual.name || detectedName,
      detectedName,
      detectedRole,
      detectedFrom,
      isNewParticipant: false,
      personId: manual.id,
      personName: manual.name || null,
      personRole: formatParticipantRole(manual.role || detectedRole),
      suggestedPersonId: manual.id,
      suggestedPersonName: manual.name || null,
      suggestedPersonRole: formatParticipantRole(manual.role || detectedRole),
      linkSource: "manual",
      sampleStartSec: hasRealSample ? d.sampleStartSec : null,
      sampleEndSec: hasRealSample ? d.sampleEndSec : null,
      sampleEstimated: !hasRealSample,
      voiceCapture: d.voiceCapture || "not_implemented",
      voiceProfileKey: d.voiceProfileKey || manual.id
    };
  }

  return {
    id: d.id || `spk_${index}`,
    label: detectedName,
    detectedName,
    detectedRole,
    detectedFrom,
    isNewParticipant: isNew,
    personId: null,
    personName: null,
    personRole: null,
    suggestedPersonId: suggested ? suggested.id : null,
    suggestedPersonName: suggested ? suggested.name : null,
    suggestedPersonRole: suggested
      ? formatParticipantRole(suggested.role || detectedRole)
      : null,
    linkSource: suggested ? "suggestion" : "unlinked",
    sampleStartSec: hasRealSample ? d.sampleStartSec : null,
    sampleEndSec: hasRealSample ? d.sampleEndSec : null,
    sampleEstimated: !hasRealSample,
    voiceCapture: "not_implemented",
    voiceProfileKey: null
  };
}

function dedupeSpeakerRows(rows) {
  const byName = new Map();
  const byPerson = new Map();

  const absorb = row => {
    const nameKey = normalizeNameKey(row.detectedName || row.label || row.name);
    const personKey = row.personId || null;
    let prev = null;
    if (personKey && byPerson.has(personKey)) {
      prev = byPerson.get(personKey);
    } else if (nameKey && byName.has(nameKey)) {
      prev = byName.get(nameKey);
    }

    if (!prev) {
      const next = { ...row };
      if (nameKey) byName.set(nameKey, next);
      if (personKey) byPerson.set(personKey, next);
      return;
    }

    const spoke =
      prev.detectedFrom === "transcript" || row.detectedFrom === "transcript";
    let manual = null;
    if (prev.linkSource === "manual") manual = prev;
    else if (row.linkSource === "manual") manual = row;
    const personId = manual ? manual.personId || null : null;
    const suggestedPersonId =
      (manual && manual.personId) ||
      prev.suggestedPersonId ||
      row.suggestedPersonId ||
      prev.personId ||
      row.personId ||
      null;
    let linkSource = "unlinked";
    if (manual) linkSource = "manual";
    else if (suggestedPersonId) linkSource = "suggestion";
    const merged = {
      ...prev,
      ...row,
      id: prev.id,
      detectedName: prev.detectedName || row.detectedName,
      label: (manual && manual.personName) || prev.label || row.label,
      personId,
      personName: manual ? manual.personName || null : null,
      personRole: manual
        ? formatParticipantRole(manual.personRole || manual.detectedRole)
        : null,
      suggestedPersonId,
      suggestedPersonName:
        (manual && manual.personName) ||
        prev.suggestedPersonName ||
        row.suggestedPersonName ||
        prev.personName ||
        row.personName ||
        null,
      suggestedPersonRole: formatParticipantRole(
        (manual && manual.personRole) ||
          prev.suggestedPersonRole ||
          row.suggestedPersonRole ||
          prev.detectedRole ||
          row.detectedRole
      ),
      detectedRole: formatParticipantRole(
        prev.detectedRole || row.detectedRole
      ),
      detectedFrom: spoke
        ? "transcript"
        : prev.detectedFrom || row.detectedFrom,
      linkSource,
      isNewParticipant: Boolean(spoke && !suggestedPersonId && !personId)
    };

    if (nameKey) byName.set(nameKey, merged);
    if (personKey) byPerson.set(personKey, merged);
    const otherName = normalizeNameKey(merged.detectedName);
    if (otherName) byName.set(otherName, merged);
    if (merged.personId) byPerson.set(merged.personId, merged);
  };

  (rows || []).forEach(absorb);
  const unique = [];
  const seen = new Set();
  byName.forEach(row => {
    const key = row.personId || normalizeNameKey(row.detectedName || row.label);
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(row);
  });
  return unique.map((row, index) => ({ ...row, id: `spk_${index}` }));
}

/**
 * União: falantes da transcrição + participantes da reunião (sem duplicar).
 * Quem só aparece na fala e não está no roster fica como novo (vínculo pendente).
 */
export function buildSpeakersFromAta(ata) {
  if (!ata) return [];
  const options = listPersonOptions(ata);
  const knownNames = options.map(o => o.name);
  const transcriptText = normalizeAttributedTranscript(ata.transcriptText, {
    knownNames
  });
  const fromTranscript = parseSpeakersFromTranscript(transcriptText).map(d => {
    const match = findPersonMatch(d.name, options);
    return {
      ...d,
      // Match automático só como previsão — operador valida
      personId: null,
      suggestedPersonId: match ? match.id : null,
      suggestedPersonName: match ? match.name : null,
      role: (match && match.role) || d.role,
      detectedFrom: "transcript",
      linkSource: match ? "suggestion" : "unlinked"
    };
  });
  const fromRoster = speakersFromParticipants(ata).map(d => ({
    ...d,
    personId: null,
    suggestedPersonId: d.personId || null,
    suggestedPersonName: d.name,
    detectedFrom: "roster",
    linkSource: d.personId ? "suggestion" : "unlinked"
  }));

  const merged = [];
  const seenNames = new Set();
  const seenPersons = new Set();

  const findMergedIndex = d => {
    const nameKey = normalizeNameKey(d.name);
    if (!nameKey) return -1;
    const idx = merged.findIndex(x => {
      const xn = normalizeNameKey(x.name);
      if (!xn) return false;
      if (xn === nameKey) return true;
      if (d.personId && x.personId && d.personId === x.personId) return true;
      // Nome contido / mesmo prenome+sobrenome
      if (xn.includes(nameKey) || nameKey.includes(xn)) return true;
      const at = nameKey.split(/\s+/);
      const bt = xn.split(/\s+/);
      return (
        at.length >= 2 &&
        bt.length >= 2 &&
        at[0] === bt[0] &&
        at[at.length - 1] === bt[bt.length - 1]
      );
    });
    return idx;
  };

  const pushUnique = d => {
    const nameKey = normalizeNameKey(d.name);
    if (!nameKey) return;
    if (d.personId && seenPersons.has(d.personId)) return;

    const existingIdx = findMergedIndex(d);
    if (existingIdx >= 0) {
      const prev = merged[existingIdx];
      merged[existingIdx] = {
        ...prev,
        ...d,
        name: prev.name || d.name,
        role: d.role || prev.role,
        personId: null,
        suggestedPersonId:
          prev.suggestedPersonId ||
          d.suggestedPersonId ||
          prev.personId ||
          d.personId ||
          null,
        suggestedPersonName:
          prev.suggestedPersonName ||
          d.suggestedPersonName ||
          prev.name ||
          d.name,
        detectedFrom:
          prev.detectedFrom === "transcript" || d.detectedFrom === "transcript"
            ? "transcript"
            : d.detectedFrom || prev.detectedFrom,
        linkSource:
          prev.linkSource === "manual" || d.linkSource === "manual"
            ? "manual"
            : "suggestion"
      };
      seenNames.add(normalizeNameKey(merged[existingIdx].name));
      return;
    }

    if (seenNames.has(nameKey)) return;
    seenNames.add(nameKey);
    merged.push(d);
  };

  fromTranscript.forEach(pushUnique);
  fromRoster.forEach(pushUnique);

  if (!merged.length) {
    options.forEach(o =>
      pushUnique({
        name: o.name,
        role: o.role,
        personId: null,
        suggestedPersonId: o.id,
        suggestedPersonName: o.name,
        detectedFrom: "roster",
        linkSource: "suggestion"
      })
    );
  }

  return dedupeSpeakerRows(
    merged.map((d, index) => toSpeakerRow(d, index, options))
  );
}

function speakersLookBroken(speakers) {
  return (speakers || []).some(s => {
    const name = String(s.detectedName || s.label || "").trim();
    const tokens = name.split(/\s+/).filter(Boolean);
    // Sobrenome sozinho ou legado "Speaker N"
    if (/^Speaker\s+\d+/i.test(name)) return true;
    if (tokens.length === 1 && name.length <= 12) return true;
    return false;
  });
}

/** Mesmo nome como "novo" + "convidado" = lista legada duplicada. */
function speakersHaveDuplicateNames(speakers) {
  const seen = new Set();
  return (speakers || []).some(s => {
    const key = normalizeNameKey(
      s.detectedName || s.personName || s.label || s.name
    );
    if (!key) return false;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
}

export function ensureAtaSpeakers(ata) {
  if (!ata) return ata;
  const hasTurns = transcriptHasSpeakerTurns(ata.transcriptText);
  const existing = ata.speakers || [];

  const previousManual = new Map();
  existing.forEach(s => {
    if (s.linkSource === "manual" && s.personId) {
      previousManual.set(
        String(s.detectedName || s.label || "")
          .trim()
          .toLowerCase(),
        s
      );
    }
  });

  const shouldRebuild =
    !existing.length ||
    hasTurns ||
    speakersLookBroken(existing) ||
    speakersHaveDuplicateNames(existing) ||
    existing.some(s => /^Speaker\s+\d+/i.test(String(s.label || "")));

  if (!shouldRebuild) {
    const options = listPersonOptions(ata);
    const speakersKept = attachMockVoiceSamples(
      dedupeSpeakerRows(
        existing.map((s, index) => {
          // Legado auto/roster → previsão (operador valida)
          if (s.linkSource === "manual" && s.personId) {
            return {
              ...s,
              detectedRole: formatParticipantRole(
                s.detectedRole || s.personRole
              ),
              personRole: formatParticipantRole(s.personRole || s.detectedRole),
              sampleStartSec: null,
              sampleEndSec: null,
              sampleEstimated: true
            };
          }
          return toSpeakerRow(
            {
              ...s,
              name: s.detectedName || s.label,
              role: s.detectedRole || s.personRole,
              personId: s.personId,
              suggestedPersonId: s.suggestedPersonId || s.personId,
              linkSource: "suggestion",
              detectedFrom: s.detectedFrom || "transcript"
            },
            index,
            options
          );
        })
      ),
      ata.transcriptText || ""
    );
    return {
      ...ata,
      speakers: speakersKept,
      voicesLinked:
        speakersKept.length > 0 &&
        speakersKept.every(s => s.personId && s.linkSource === "manual")
    };
  }

  let speakers = buildSpeakersFromAta(ata);
  if (previousManual.size) {
    speakers = speakers.map(s => {
      const prev = previousManual.get(normalizeNameKey(s.detectedName || ""));
      if (!prev) return s;
      return {
        ...s,
        personId: prev.personId,
        personName: prev.personName,
        personRole: formatParticipantRole(prev.personRole || s.personRole),
        suggestedPersonId: prev.personId,
        suggestedPersonName: prev.personName,
        suggestedPersonRole: formatParticipantRole(
          prev.personRole || s.suggestedPersonRole
        ),
        linkSource: "manual",
        label: prev.personName || s.label,
        isNewParticipant: false,
        voiceProfileKey: prev.voiceProfileKey || prev.personId
      };
    });
  }

  speakers = dedupeSpeakerRows(speakers);

  const knownNames = listPersonOptions(ata).map(o => o.name);
  const transcriptText = hasTurns
    ? normalizeAttributedTranscript(ata.transcriptText, { knownNames })
    : ata.transcriptText;

  // Reparse speakers se o reparo de turnos alterou a transcrição
  if (transcriptText !== (ata.transcriptText || "")) {
    speakers = buildSpeakersFromAta({ ...ata, transcriptText });
    if (previousManual.size) {
      speakers = speakers.map(s => {
        const prev = previousManual.get(normalizeNameKey(s.detectedName || ""));
        if (!prev) return s;
        return {
          ...s,
          personId: prev.personId,
          personName: prev.personName,
          personRole: formatParticipantRole(prev.personRole || s.personRole),
          suggestedPersonId: prev.personId,
          suggestedPersonName: prev.personName,
          suggestedPersonRole: formatParticipantRole(
            prev.personRole || s.suggestedPersonRole
          ),
          linkSource: "manual",
          label: prev.personName || s.label,
          isNewParticipant: false,
          voiceProfileKey: prev.voiceProfileKey || prev.personId
        };
      });
    }
    speakers = dedupeSpeakerRows(speakers);
  }

  speakers = attachMockVoiceSamples(speakers, transcriptText || "");

  return {
    ...ata,
    transcriptText,
    speakers,
    voicesLinked:
      speakers.length > 0 &&
      speakers.every(s => s.personId && s.linkSource === "manual")
  };
}

export function linkSpeaker(speakers, speakerId, personId, options) {
  const person = (options || []).find(o => o.id === personId) || null;
  return (speakers || []).map(s => {
    if (s.id !== speakerId) return s;
    if (!personId) {
      return {
        ...s,
        personId: null,
        personName: null,
        personRole: null,
        linkSource: s.suggestedPersonId ? "suggestion" : "unlinked",
        voiceProfileKey: null,
        voiceCapture: "not_implemented"
      };
    }
    return {
      ...s,
      personId: person ? person.id : personId,
      personName: person ? person.name : null,
      personRole: person ? person.role : null,
      suggestedPersonId: person ? person.id : personId,
      suggestedPersonName: person ? person.name : s.suggestedPersonName,
      suggestedPersonRole: person ? person.role : s.suggestedPersonRole,
      linkSource: "manual",
      label: person ? person.name : s.label,
      isNewParticipant: false,
      // Chave estável da pessoa — NÃO é embedding de áudio (ainda)
      voiceProfileKey: person ? person.id : null,
      voiceCapture: "not_implemented"
    };
  });
}

/**
 * Troca nome do falante na transcrição/textos da ata (prefixo "Nome (papel):" + ocorrências).
 */
export function rewriteNameInText(text, fromName, toName, toRole) {
  if (text == null || text === "") return text;
  const from = String(fromName || "").trim();
  const to = String(toName || "").trim();
  if (!from || !to) return text;
  const esc = escapeRegExp(from);
  let out = String(text);
  out = out.replace(
    new RegExp(`^${esc}\\s*\\(([^)]*)\\):`, "gm"),
    (match, oldRole) => `${to} (${toRole || oldRole || "participante"}):`
  );
  if (from !== to) {
    out = out.split(from).join(to);
  }
  return out;
}

/**
 * Legado: vínculo no falante inteiro. Preferir applyTurnPersonToAta.
 */
export function applySpeakerPersonToAta(ata, speakerId, personId) {
  if (!ata || !speakerId) return null;
  const options = listPersonOptions(ata);
  const speakersBefore = ata.speakers || [];
  const target = speakersBefore.find(s => s.id === speakerId);
  if (!target) return null;

  const speakers = linkSpeaker(speakersBefore, speakerId, personId, options);
  const voicesLinked = computeVoicesLinkedFromTurns(
    ata.transcriptText,
    ata.turnLinks
  );

  return {
    speakers,
    voicesLinked
  };
}

export function speakersSummary(speakers, transcriptText, turnLinks) {
  const list = speakers || [];
  const turns = parseTranscriptTurns(transcriptText);
  const links = turnLinks || {};
  const turnsLinked = turns.filter(
    t =>
      links[t.id] && links[t.id].linkSource === "manual" && links[t.id].personId
  ).length;
  const turnsSuggested = turns.filter(
    t =>
      !(links[t.id] && links[t.id].linkSource === "manual") &&
      ((links[t.id] && links[t.id].suggestedPersonId) ||
        list.some(
          s =>
            normalizeNameKey(s.detectedName) === normalizeNameKey(t.name) &&
            s.suggestedPersonId
        ))
  ).length;
  const linked = list.filter(s => s.personId && s.linkSource === "manual")
    .length;
  const suggested = list.filter(s => !s.personId && s.suggestedPersonId).length;
  const fromTranscript = list.filter(s => s.detectedFrom === "transcript")
    .length;
  const newcomers = list.filter(s => s.isNewParticipant).length;
  const withMinSamples = countSpeakersWithMinSamples(
    list,
    transcriptText,
    MIN_VOICE_SAMPLES,
    links
  );
  const speakersReady = list.filter(s => {
    const samples = countConfirmedSamplesForSpeaker(s, links, transcriptText);
    const pending = countPendingTurnsForSpeaker(s, links, transcriptText);
    return samples >= MIN_VOICE_SAMPLES && pending === 0;
  }).length;
  return {
    total: list.length,
    linked,
    suggested,
    unlinked: list.length - linked,
    fromTranscript,
    fromRoster: list.filter(s => s.detectedFrom === "roster").length,
    newcomers,
    withMinSamples,
    speakersReady,
    minSamples: MIN_VOICE_SAMPLES,
    turnsTotal: turns.length,
    turnsLinked,
    turnsSuggested,
    turnsUnlinked: Math.max(0, turns.length - turnsLinked)
  };
}
